import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Percent, Clock, PiggyBank, AlertCircle, CreditCard, Shield } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент общего обзора бюджета
 */
const BudgetOverview = ({ results, formData }) => {
  if (!results) return null;

  // Определяем стиль для баланса (положительный/отрицательный)
  const balanceStyle = results.balance >= 0 
    ? 'text-emerald-600 dark:text-emerald-400' 
    : 'text-red-600 dark:text-red-400';

  // Определяем статус бюджета
  const getStatusText = (status) => {
    switch (status) {
      case 'excellent': return 'Отличное';
      case 'good': return 'Хорошее';
      case 'fair': return 'Удовлетворительное';
      case 'poor': return 'Требует внимания';
      default: return 'Нейтральное';
    }
  };

  // Определяем класс стиля для статуса
  const getStatusClass = (status) => {
    switch (status) {
      case 'excellent': return 'text-emerald-600 dark:text-emerald-400';
      case 'good': return 'text-blue-600 dark:text-blue-400';
      case 'fair': return 'text-amber-600 dark:text-amber-400';
      case 'poor': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <ToolResult title="Результаты анализа бюджета">
      {/* Ключевые показатели */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Баланс бюджета */}
        <div className="rounded-lg bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/50 dark:to-slate-800/50 p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Баланс бюджета
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${balanceStyle}`} aria-live="polite">
              {formatCurrency(results.balance)} ₽
            </span>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              / месяц
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {results.balance >= 0 
              ? 'Положительный баланс позволяет создавать сбережения и инвестировать' 
              : 'Отрицательный баланс требует сокращения расходов или увеличения доходов'}
          </p>
        </div>

        {/* Финансовое состояние */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Shield className="w-4 h-4"/>
            Финансовое состояние
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${getStatusClass(results.status)}`} aria-live="polite">
              {getStatusText(results.status)}
            </span>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              ({results.financialHealthScore} / 100)
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {results.recommendations && results.recommendations[0]}
          </p>
        </div>
      </div>

      {/* Основные показатели */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Общий доход" 
          value={`${formatCurrency(results.totalIncome)} ₽/мес`} 
          color="emerald" 
          icon={<TrendingUp className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общие расходы" 
          value={`${formatCurrency(results.totalExpenses)} ₽/мес`} 
          color="red" 
          icon={<TrendingDown className="w-4 h-4"/>}
        />
        
        {results.taxAmount > 0 && (
          <ResultItem 
            label="НДФЛ (13%)" 
            value={`${formatCurrency(results.taxAmount)} ₽/мес`} 
            color="orange" 
            icon={<Percent className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Обязательные расходы" 
          value={`${formatCurrency(results.essentialExpenses)} ₽/мес (${Math.round(results.essentialRatio * 100)}%)`} 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Необязательные расходы" 
          value={`${formatCurrency(results.nonEssentialExpenses)} ₽/мес (${Math.round(results.nonEssentialRatio * 100)}%)`} 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Сбережения" 
          value={`${formatCurrency(results.savingsAmount)} ₽/мес (${formData.distribution.savingsPercentage}%)`} 
          color="blue" 
          icon={<PiggyBank className="w-4 h-4"/>}
        />
        
        {results.investments.enabled && (
          <ResultItem 
            label="Инвестиции" 
            value={`${formatCurrency(results.investmentsAmount)} ₽/мес (${formData.distribution.investmentsPercentage}%)`} 
            color="purple" 
            icon={<TrendingUp className="w-4 h-4"/>}
          />
        )}

        {/* Финансовые коэффициенты */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Финансовые коэффициенты</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Доля расходов от дохода:</span>
              <span className={`font-medium ${results.expenseRatio <= 0.8 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {formatNumber(results.expenseRatio * 100, 0)}%
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Доля сбережений от дохода:</span>
              <span className={`font-medium ${results.savingsRate >= 0.1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {formatNumber(results.savingsRate * 100, 0)}%
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Коэффициент покрытия расходов:</span>
              <span className={`font-medium ${results.incomeToExpenseRatio >= 1.1 ? 'text-emerald-600 dark:text-emerald-400' : results.incomeToExpenseRatio >= 1 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatNumber(results.incomeToExpenseRatio, 2)}x
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Финансовая свобода:</span>
              <span className={`font-medium ${results.disposableIncome >= results.totalIncome * 0.2 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {formatNumber(results.disposableIncome / results.totalIncome * 100, 0)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Рекомендации */}
      {results.recommendations && results.recommendations.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Рекомендации по улучшению бюджета</h4>
              <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1.5 list-disc list-inside">
                {results.recommendations.map((recommendation, index) => (
                  <li key={index}>{recommendation}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Чрезвычайный фонд */}
      {results.emergencyFund.enabled && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded p-4 mb-4">
          <div className="flex items-start">
            <Shield className="text-amber-500 dark:text-amber-400 w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center">
                Чрезвычайный фонд
                <Clock className="w-4 h-4 ml-2" />
                {results.emergencyFund.monthsToTarget < Infinity ? (
                  <span className="ml-2 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full">
                    {results.emergencyFund.monthsToTarget} мес. до цели
                  </span>
                ) : (
                  <span className="ml-2 text-xs bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-0.5 rounded-full">
                    Недостаточно сбережений
                  </span>
                )}
              </h4>
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-amber-600 dark:text-amber-400">Текущий размер:</span>
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {formatCurrency(results.emergencyFund.currentAmount)} ₽
                </span>
              </div>
              <div className="flex justify-between mb-3 text-sm">
                <span className="text-amber-600 dark:text-amber-400">Целевой размер:</span>
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {formatCurrency(results.emergencyFund.targetAmount)} ₽
                </span>
              </div>
              <div className="relative w-full h-2 bg-amber-200 dark:bg-amber-800 rounded-full overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full bg-amber-500 dark:bg-amber-600" 
                  style={{ 
                    width: `${Math.min(100, (results.emergencyFund.currentAmount / results.emergencyFund.targetAmount) * 100)}%` 
                  }}
                ></div>
              </div>
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Вы накопили {formatNumber((results.emergencyFund.currentAmount / results.emergencyFund.targetAmount) * 100, 0)}% от необходимой суммы.
                {results.emergencyFund.monthsToTarget < Infinity ? 
                  ` При текущем темпе сбережений вы достигнете цели через ${results.emergencyFund.monthsToTarget} месяцев.` : 
                  ' Увеличьте ежемесячные сбережения для формирования фонда.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </ToolResult>
  );
};

export default BudgetOverview;